import { displayPrice, getImageUrl } from "@/lib/utils";
import { Minus, Plus, Trash2 } from "lucide-react";
import { CartEntry } from "./types";

interface CartItemProps {
  entry: CartEntry;
  currency: string;
  onUpdateQuantity: (entryId: string, delta: number) => void;
  onRemoveItem: (entryId: string) => void;
  /** Add-only edit: existing (committed) lines can't be removed or reduced. */
  addOnly?: boolean;
}

const CartItem: React.FC<CartItemProps> = ({
  entry,
  currency,
  onUpdateQuantity,
  onRemoveItem,
  addOnly = false,
}) => {
  // A line hydrated from the order (existingIri) is removed via the trash icon,
  // not by counting down to zero — so its minus stops at the minimum quantity.
  // Add-only: locked at the committed quantity (no trash). Full edit: stops at
  // 1 (trash removes). A freshly-added line keeps the create-flow behaviour
  // where minus at 1 removes it.
  const isExisting = !!entry.existingIri;
  const isLocked = addOnly && isExisting;
  const minQty = isLocked
    ? entry.originalQuantity ?? entry.quantity
    : isExisting
      ? 1
      : 0;
  const canDecrease = entry.quantity > minQty;
  return (
    <div className="flex items-start gap-3 py-3 px-2">
      <img
        src={getImageUrl(entry.imageUrl)}
        alt={entry.name}
        className="rounded-lg h-14 w-14 object-cover flex-shrink-0"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-card-foreground">
              {entry.name}
            </p>
            <p className="text-xs font-medium mt-1">
              {displayPrice(entry.unitPrice * entry.quantity, currency)}
            </p>
            {entry.selectedOptionLabels.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {entry.selectedOptionLabels.join(", ")}
              </p>
            )}
            {entry.additionalDemands && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Note: {entry.additionalDemands}
              </p>
            )}
          </div>

          <div className="flex flex-col items-end flex-shrink-0">
            {!isLocked && (
              <button
                onClick={() => onRemoveItem(entry.id)}
                className="p-1 text-muted-foreground hover:text-destructive transition-colors"
              >
                <Trash2 size={14} />
              </button>
            )}
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={() => canDecrease && onUpdateQuantity(entry.id, -1)}
                disabled={!canDecrease}
                title={
                  canDecrease
                    ? undefined
                    : isLocked
                      ? "Article déjà envoyé en cuisine — non modifiable"
                      : isExisting
                        ? "Utilisez la corbeille pour retirer cet article"
                        : undefined
                }
                className="w-7 h-7 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                <Minus size={14} />
              </button>
              <span className="text-sm font-medium">{entry.quantity}</span>
              <button
                onClick={() => onUpdateQuantity(entry.id, 1)}
                className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
